import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import config from '../../config'
import { selectExam } from '../../features/exams/selectors'
import {
	selectIsDoctor,
	selectIsRefractionist,
} from '../../features/users/selectors'
import useEclipsMetadata from '../../hooks/useEclipsMetadata'
import { isTodayZoned } from '../../libs/time'
import { useTeloSelector } from '../../store'
import { pageWrapperPadding } from '../../styleguide/CommonPageComponents'
import theme from '../../styleguide/theme'
import { IMAGES } from '../../utils/assetRegistry'
import { RightCol } from './common'
import { LeftButton } from './LeftButton'
import {
	RightColumnContent,
	RightColumnContentProps,
} from './RightColumnContent'
import { useCheckCurrentPage } from '../../routing/teloRouterUtils'
import InactivityModal from './InactivityModal'
import { convertToMiliSeconds } from '../../utils/TimeConverterToMiliSeconds'
import { selectUserRole } from '../../features/auth/selectors'
import { selectStore } from '../../features/stores/selectors'
import { selectStorePractice } from '../../features/practices/selectors'
import { selectInStore } from '../../features/app/selectors'
import { selectTimeouts } from '../../features/timeouts/selectors'
import { HourAndMinute } from '../../model/timeoutConfig'

const ConnectHeader = styled.header`
	padding: ${theme.spacing(2)};
	display: grid;
	grid-template-columns: 41% 18% 41%;
	width: 100%;
	position: relative;
	z-index: ${theme.zIndex.header};
`

const EclipsHeader = styled.header`
	${pageWrapperPadding}
	padding-top: ${theme.spacing(2)};
	padding-bottom: ${theme.spacing(4)};
	display: flex;
	width: 100%;
	z-index: ${theme.zIndex.header};
	background-color: ${theme.palette.background.default};
	justify-content: space-between;
`

const Logo = styled.img<{ visionx: boolean }>`
	width: ${props => (props.visionx === true ? '70%' : '100%')};
	height: auto;
	display: block;
	grid-column: 2;
	place-self: center center;
`

const LeftCol = styled.div`
	grid-column: 1;
	place-self: center left;
	display: flex;
	align-items: center;
`

const AzureStatusIndicatorContainer = styled.div`
	margin-left: ${theme.spacing(2)};
	margin-top: ${theme.spacing(2)};
`

interface Props {
	hideHomeIcon?: boolean
	hideProfileMenu?: boolean
	hideUnavailable?: boolean
	hideLogo?: boolean
	examId?: string
	centered?: boolean
	logoSrc?: string
	isMissionControlOpen?: boolean
	openMissionControl?: () => void
}

const Header: React.FC<Props> = ({
	hideHomeIcon,
	hideProfileMenu,
	logoSrc: logoSrcInput,
	hideUnavailable,
	isMissionControlOpen,
	hideLogo,
	openMissionControl,
	examId,
}) => {
	const logoUrl =
		config.region === 'GEMINI'
			? IMAGES.VISIONX_LOGO_URL
			: IMAGES.CONNECT_LOGO_URL
	const logoSrc = logoSrcInput ?? logoUrl
	const exam = useTeloSelector(selectExam(examId || ''))
	const inStore = useTeloSelector(selectInStore)
	const remoteConfig = useTeloSelector(selectTimeouts)

	const $checkedInAt = exam?.checkedInAt
	const $timezone = exam?.store.timezone
	const isTodayExam = useMemo(() => {
		if (!$checkedInAt || !$timezone) {
			return false
		}
		return isTodayZoned($checkedInAt, $timezone)
	}, [$checkedInAt, $timezone])

	const { isWorklistPage, isExamPage } = useCheckCurrentPage()
	const isDoctor = useTeloSelector(selectIsDoctor)
	const isRefractionist = useTeloSelector(selectIsRefractionist)
	const eclipsMetadata = useEclipsMetadata()
	const isAppInsideEclips = eclipsMetadata !== undefined
	const userRole = useTeloSelector(selectUserRole)
	const store = useTeloSelector(selectStore)
	const practice = useTeloSelector(selectStorePractice(store?._id!))

	const showReloadButton = isWorklistPage && !isMissionControlOpen

	const rightColumnContentProps: RightColumnContentProps = {
		hideUnavailable,
		isAppInsideEclips,
		isDoctor,
		isRefractionist,
		isWorklistPage,
		hideProfileMenu,
		isMissionControlOpen,
		openMissionControl,
	}

	//Inactivity modal
	const [timeFromBackend, setTimeFromBackend] = useState<
		HourAndMinute | undefined
	>()
	const [renderModal, setRenderModal] = useState<boolean>(false)
	const [timeInMiliseconds, setTimeInMiliseconds] = useState<number>(0)

	const inactivityTimeout: HourAndMinute | undefined = useMemo(
		() => practice[0]?.inactivityTimeoutConfig,
		[practice],
	)

	useEffect(() => {
		!inStore && setTimeFromBackend(remoteConfig.inactivityTimeoutConfig)
	}, [inStore, remoteConfig.inactivityTimeoutConfig])

	useEffect(() => {
		inStore && inactivityTimeout && setTimeFromBackend(inactivityTimeout)
	}, [inStore, inactivityTimeout])

	useEffect(() => {
		if (
			['Technician', 'Doctor', 'Refractionist', 'GlobalAdmin'].includes(
				userRole,
			) &&
			timeFromBackend
		) {
			const miliseconds = convertToMiliSeconds(timeFromBackend)
			setTimeInMiliseconds(miliseconds)

			setRenderModal(true)
		}
	}, [timeFromBackend, userRole])

	return !isAppInsideEclips ? (
		<ConnectHeader>
			<LeftCol>
				<LeftButton
					showReloadButton={showReloadButton}
					showHomeButton={!hideHomeIcon}
				/>
			</LeftCol>
			{renderModal ? <InactivityModal time={timeInMiliseconds} /> : null}
			{hideLogo ? null : (
				<Logo
					alt="Luxottica Optometrist"
					src={logoSrc}
					visionx={config.region === 'GEMINI'}
				/>
			)}
			<RightCol>
				<RightColumnContent {...rightColumnContentProps} />
			</RightCol>
		</ConnectHeader>
	) : (
		<EclipsHeader>
			<div>
				<LeftButton
					showReloadButton={showReloadButton}
					showHomeButton={false}
				/>
			</div>
			<div>
				<RightColumnContent {...rightColumnContentProps} />
			</div>
		</EclipsHeader>
	)
}

export default Header
